import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { RefreshCw } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { NotPermitted, RecordSection } from '@/design';
import { ApiError } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type {
  IAdminDisclosure,
  IAdminParticipantDetail,
  IAdminParticipantHub
} from '../../api/participant-contracts';
import type {
  IManualWorkItem,
  IWorkItemAssignee,
  IWorkItemRow,
  IWorkItemWithEvents
} from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { absent } from '../../components/account-facts';
import {
  LoadError,
  Pager,
  PastTheEnd,
  Section,
  TableSkeleton,
  useAdminData
} from '../../components/admin-page';
import { workItemPath } from '../../paths';
import { triggerLabel, UNCLOSED_STATES } from '../../work-items';
import { WorkItemEvents } from '../compliance/work-item-pane';
import {
  controlRefusal,
  NOT_PERMITTED_ASSIGN,
  NOT_PERMITTED_RAISE,
  TEXT_MAX
} from '../compliance/work-item-writes';
import { WorkItemsTable, WorkItemStateChip } from '../compliance/work-items-table';
import { disclosureSummaryOf } from './disclosure-summary';
import { disclosureWords } from './disclosures';
import { MemberPart } from './household';
import { HUB_MEMORY_PAGE } from './hub-memory';
import { useListPaging } from './client-page';

/**
 * The household's work items on the person hub (SR-222, F9's R10) — each member's open items, a
 * manual one raised on it ("New task", CMP-15), and every item the household has ever held, with
 * its events (the compliance history, CMP-13). On a person's record page (SR-391) the members are
 * the person alone, and the history is still the household's — survey-api's read of it is.
 * Drawn after Disclosures (the roadmap's U1), and
 * **always**: whether a member holds an item takes a read to know —
 * the Salesforce tasks' rule.
 *
 * **Its own reads, below a loaded hub**, the activity's three arms each: per member,
 * `GET /admin/work-items?participantId=…` over the three unclosed states, earliest due first
 * (survey-api's `due_on`), paged by survey-api; and once for the household,
 * `GET …/participants/{opened}/work-items`, folded under its own section, closed by default
 * (`RecordSection`, remembered per viewer). An item opens in the compliance queue's pane, full
 * width — the queue is where it is decided; this section names and raises.
 *
 * **Re-read on a write and after its own, never on the poll.** A BrokerCheck or IAPD answer the
 * poll brings can raise an item (survey-api runs its concern criteria as it applies the answer),
 * but a household's items do not justify multiplying the five-second poll by the household — the
 * disclosures' argument. So the section says that an answer may have raised one and offers "Read
 * again", the hand-driven path ("Ask registries again" above, then this), as "Check again" is
 * the poll's.
 *
 * **New task is the hub's write rules.** Subject and note required, their bounds said and never
 * enforced; a disclosure to attach chosen from **that member's disclosures as the Disclosures
 * section drew them** (`disclosuresOf`), never a second read of their route; whom to assign it to
 * from `/assignees`. The queue it lands in is survey-api's (design §4.1), said beside the form with
 * the item's subject as a link. A refusal survey-api can name — 400 at a field, 404 for a
 * disclosure outside the participant's scope, 422 for an assignee or a participant with no queue
 * survey — is said in its words inside the form, what was typed kept; an answer that never arrived
 * re-reads the section and asks to be checked, since raising it again might raise it twice.
 */
export function WorkItemsSection({
  hub,
  members,
  written,
  disclosuresOf,
  anchor,
  newTaskAsked = 0,
  onOpenItems
}: {
  hub: IAdminParticipantHub;
  members: IAdminParticipantDetail[];
  /** The page's write count — a write elsewhere on the page re-reads this section. */
  written: number;
  /** A member's disclosures as the Disclosures section drew them (`useDrawnDisclosures`). */
  disclosuresOf: (participantId: string) => IAdminDisclosure[] | undefined;
  anchor: string;
  /** Counts the Actions menu's "New task" presses (SR-391): each opens the opened participant's
   *  form, the cursor in its subject. */
  newTaskAsked?: number;
  /** Hands each member's first page of open items up as it lands — the record summary's
   *  compliance line (SR-400), never a second read of the queue. */
  onOpenItems?: (participantId: string, read: IOpenItemsRead) => void;
}) {
  const multi = members.length > 1;
  // "Read again" and this section's own writes: the reads below follow it with the page's count.
  const [round, setRound] = React.useState(0);
  const readAgain = React.useCallback(() => setRound((count) => count + 1), []);
  const reading = `${written} ${round}`;
  const assignees = useAdminData((signal) => workItemsApi.workItemAssignees(signal), []);

  return (
    <Section
      title="Work items"
      anchor={anchor}
      description={
        'Compliance work on this household: each member’s open items, earliest due first, and ' +
        'every item it has held. An item is decided in the compliance queue, where it opens. ' +
        'An item a BrokerCheck or IAPD answer raised after the page was read shows on “Read again”.'
      }
      aside={
        // In the title row beside the "?", its why there (SR-407): a line of its own above
        // every table asked a question the reader rarely has.
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 gap-1 px-1.5 text-xs text-muted-foreground"
          onClick={readAgain}
        >
          <RefreshCw className="size-3.5" aria-hidden />
          Read again
        </Button>
      }
    >
      <CardContent className="grid gap-8">
        {members.map((member, index) => (
          <MemberPart
            key={member.participantId}
            member={member}
            opened={index === 0}
            multi={multi}
          >
            <OpenItems
              participantId={member.participantId}
              reading={reading}
              onOpenItems={onOpenItems}
            />
            <NewTask
              participantId={member.participantId}
              disclosures={disclosuresOf(member.participantId)}
              withDisclosures={Boolean(disclosureSummaryOf(hub, member.participantId))}
              assignees={assignees.data}
              assigneesFailed={assignees.error !== null}
              onRaised={readAgain}
              asked={index === 0 ? newTaskAsked : 0}
            />
          </MemberPart>
        ))}
        <RecordSection
          page={HUB_MEMORY_PAGE}
          sectionKey="work-items.history"
          title="U4 / compliance history"
          level={3}
          defaultOpen={false}
          filled={0}
          total={0}
        >
          <HouseholdHistory
            participantId={hub.participant.participantId}
            multi={multi}
            reading={reading}
          />
        </RecordSection>
      </CardContent>
    </Section>
  );
}

/** A member's open items as last read — the first page (null before one landed), how many there
 *  are in all, and the newest read's failure. */
export interface IOpenItemsRead {
  items: Nullable<IWorkItemRow[]>;
  total: Nullable<number>;
  error: Nullable<string>;
}

/** One member's unclosed items, earliest due first, paged by survey-api. */
function OpenItems({
  participantId,
  reading,
  onOpenItems
}: {
  participantId: string;
  reading: string;
  onOpenItems?: (participantId: string, read: IOpenItemsRead) => void;
}) {
  const { page, pageSize, setPage, more } = useListPaging();
  const { data, error, reload } = useAdminData(
    (signal) =>
      workItemsApi.listWorkItems(
        {
          participantId,
          state: [...UNCLOSED_STATES],
          sort: 'due_on',
          page,
          pageSize
        },
        signal
      ),
    [participantId, page, pageSize, reading]
  );
  // The first page alone — earliest due first — is what the summary says; a later page turned to
  // here leaves the summary as it was.
  React.useEffect(() => {
    if (page === 1) {
      onOpenItems?.(participantId, {
        items: data?.items ?? null,
        total: data?.total ?? null,
        error
      });
    }
  }, [onOpenItems, participantId, page, data, error]);
  // The activity's rule: the pager is drawn from the page last read, so a press for the page
  // whose read failed asks again rather than setting what is already set.
  const turnTo = (target: number) => (target === page ? void reload() : setPage(target));

  return (
    <div className="grid gap-3">
      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not refresh the work items — showing them as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}
      {data === null ? (
        error ? null : <TableSkeleton rows={2} />
      ) : data.total === 0 ? (
        // No open item is one line, not a table's head over nothing (SR-407).
        <p className="text-sm">{absent('No open work items.')}</p>
      ) : (
        <>
          <WorkItemsTable
            rows={data.items}
            itemPath={(id) => workItemPath(id)}
            queueColumn
            participantColumn={false}
            state={
              data.items.length === 0
                ? {
                    kind: 'empty',
                    // Only past the end: a total of none is the line above.
                    content: (
                      <PastTheEnd
                        page={data.page}
                        pageSize={data.pageSize}
                        total={data.total}
                        counted={['open item', 'open items']}
                        onPage={turnTo}
                      />
                    )
                  }
                : undefined
            }
          />
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={turnTo}
            quiet
            onMore={more}
          />
        </>
      )}
    </div>
  );
}

/** survey-api's bound on a task's subject, said and never enforced. */
const SUBJECT_MAX = 200;

/** New task's 404, which survey-api sends with no body for an unknown participant and for a
 *  disclosure outside the participant's scope alike. */
const NOT_FOUND =
  'Not raised: the system holds no such participant, or the disclosure chosen is not one of ' +
  'theirs — this page is out of date. It was read again; check the disclosure and press again.';

/** The selects' "none" — Radix's `Select` takes no empty value. */
const NONE = '__none__';

type Raised = { kind: 'raised'; row: IWorkItemRow } | { kind: 'unknown' };

/**
 * New task on one member (CMP-15): closed to a button until pressed, then a form. What it was
 * told stays beside it after the form closes — the raised item, or an answer that never arrived.
 */
function NewTask({
  participantId,
  disclosures,
  withDisclosures,
  assignees,
  assigneesFailed,
  onRaised,
  asked
}: {
  participantId: string;
  /** The member's drawn disclosures; `undefined` while its part is not drawn. */
  disclosures: IAdminDisclosure[] | undefined;
  /** Whether the member has a CRD set — whether a disclosure could be drawn for it at all. */
  withDisclosures: boolean;
  assignees: Nullable<IWorkItemAssignee[]>;
  assigneesFailed: boolean;
  /** A task was raised, or may have been: the section reads again. */
  onRaised: () => void;
  /** The Actions menu's presses for this member — each a press of the button below. */
  asked: number;
}) {
  const [open, setOpen] = React.useState(false);
  const subjectRef = React.useRef<HTMLInputElement>(null);
  const [subject, setSubject] = React.useState('');
  const [note, setNote] = React.useState('');
  const [disclosureId, setDisclosureId] = React.useState(NONE);
  const [assignee, setAssignee] = React.useState(NONE);
  const [sending, setSending] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [raised, setRaised] = React.useState<Nullable<Raised>>(null);
  const ids = React.useId();
  // A task is raised under `responses.annotate`; naming its assignee is `work_items.assign`, which
  // survey-api refuses apart (SR-228) — so without it the choice is drawn off, the task unassigned.
  const can = useCan();
  const canAssign = can('work_items.assign');
  const canRaise = can('responses.annotate');

  // The menu's press opens the form as the button's would, and puts the cursor in the subject —
  // a viewer who cannot raise a task is drawn the menu's entry off, so never asks. Only a press
  // made since this form was mounted: a count already handled reopens nothing.
  const handled = React.useRef(asked);
  const [focusSubject, setFocusSubject] = React.useState(false);
  React.useEffect(() => {
    if (asked <= handled.current) return;
    handled.current = asked;
    setOpen(true);
    setFocusSubject(true);
  }, [asked]);
  React.useEffect(() => {
    if (!focusSubject || !open) return;
    subjectRef.current?.focus();
    setFocusSubject(false);
  }, [focusSubject, open]);

  const close = () => {
    setOpen(false);
    setSubject('');
    setNote('');
    setDisclosureId(NONE);
    setAssignee(NONE);
    setRefusal(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSending(true);
    setRefusal(null);
    const body: IManualWorkItem = {
      subject: subject.trim(),
      note: note.trim(),
      ...(disclosureId !== NONE && { disclosureId }),
      ...(assignee !== NONE && { assigneeAccountId: assignee })
    };
    try {
      const row = await workItemsApi.raiseWorkItem(participantId, body);
      setRaised({ kind: 'raised', row });
      close();
      onRaised();
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        // Bodiless: the participant or the disclosure is not what this page read. The hub's rule
        // — a 404 re-reads — with what was typed kept for a second press.
        setRefusal(NOT_FOUND);
        onRaised();
      } else if (error instanceof ApiError && error.status < 500) {
        setRefusal(controlRefusal(error));
      } else {
        // Raised or not, nobody can say: the section reads again, and the form keeps what was
        // typed for a second press once the list says it is needed.
        setRaised({ kind: 'unknown' });
        onRaised();
      }
    } finally {
      setSending(false);
    }
  };

  const blank = subject.trim() === '' || note.trim() === '';
  return (
    <div className="grid gap-3">
      {raised?.kind === 'raised' && (
        <p role="status" data-raised className="text-sm">
          Raised in the {raised.row.surveyCode} queue:{' '}
          <Link
            to={workItemPath(raised.row.id)}
            className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            {raised.row.subject}
          </Link>
        </p>
      )}
      {raised?.kind === 'unknown' && (
        <Alert>
          <AlertTitle>The answer did not arrive</AlertTitle>
          <AlertDescription>
            The task may have been raised. The list above was read again — check it there before
            raising it a second time.
          </AlertDescription>
        </Alert>
      )}
      {!open ? (
        <div className="grid justify-items-start gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canRaise}
            aria-describedby={canRaise ? undefined : `${ids}-raise`}
            onClick={() => setOpen(true)}
          >
            New task
          </Button>
          {!canRaise && <NotPermitted id={`${ids}-raise`} what={NOT_PERMITTED_RAISE} />}
        </div>
      ) : (
        <form
          onSubmit={submit}
          aria-label="New task"
          className="grid gap-3 rounded-md border border-border p-4 text-sm"
        >
          <div className="grid gap-1">
            <label htmlFor={`${ids}-subject`}>Subject</label>
            <Input
              ref={subjectRef}
              id={`${ids}-subject`}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              aria-describedby={`${ids}-subject-bound`}
            />
            <span id={`${ids}-subject-bound`} className="text-xs text-muted-foreground">
              {boundWords(subject.length, SUBJECT_MAX)}
            </span>
          </div>
          <div className="grid gap-1">
            <label htmlFor={`${ids}-note`}>Note</label>
            <Textarea
              id={`${ids}-note`}
              value={note}
              rows={4}
              onChange={(event) => setNote(event.target.value)}
              aria-describedby={`${ids}-note-bound`}
            />
            <span id={`${ids}-note-bound`} className="text-xs text-muted-foreground">
              {boundWords(note.length, TEXT_MAX)}
            </span>
          </div>
          {disclosures && disclosures.length > 0 ? (
            <div className="grid gap-1">
              <span className="text-muted-foreground">About a disclosure (optional)</span>
              <Select value={disclosureId} onValueChange={setDisclosureId}>
                <SelectTrigger aria-label="Disclosure" className="max-w-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No disclosure</SelectItem>
                  {disclosures.map((disclosure) => (
                    <SelectItem key={disclosure.id} value={disclosure.id}>
                      {disclosureWords(disclosure)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            withDisclosures &&
            disclosures === undefined && (
              <p className="text-muted-foreground">
                The disclosures are not drawn on this page, so none can be attached now.
              </p>
            )
          )}
          <div className="grid gap-1">
            <span className="text-muted-foreground">Assign to (optional)</span>
            <Select value={assignee} onValueChange={setAssignee} disabled={!canAssign}>
              <SelectTrigger
                aria-label="Assign to"
                aria-describedby={canAssign ? undefined : `${ids}-assign`}
                className="max-w-md"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Nobody</SelectItem>
                {(assignees ?? []).map((account) => (
                  <SelectItem key={account.accountId} value={account.accountId}>
                    {account.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!canAssign && <NotPermitted id={`${ids}-assign`} what={NOT_PERMITTED_ASSIGN} />}
            {assigneesFailed && (
              <span className="text-xs text-muted-foreground">
                Whom to assign to could not be read — the task can be raised unassigned.
              </span>
            )}
          </div>
          {refusal && (
            <p role="alert" className="text-[var(--color-deep-magenta)]">
              {refusal}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={blank || sending}>
              Raise task
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={close} disabled={sending}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/** "12 of 200 characters", or past the bound "212 typed — survey-api takes 200": said, never
 *  enforced — survey-api's 400 is the judge. */
function boundWords(length: number, max: number): string {
  const typed = length.toLocaleString('en-US');
  const bound = max.toLocaleString('en-US');
  return length > max
    ? `${typed} typed — the system takes ${bound}`
    : `${typed} of ${bound} characters`;
}

/** Every item the household has held, any state, each with its events — newest first, paged. */
function HouseholdHistory({
  participantId,
  multi,
  reading
}: {
  participantId: string;
  multi: boolean;
  reading: string;
}) {
  const { page, pageSize, setPage, more } = useListPaging();
  const { data, error, reload } = useAdminData(
    (signal) =>
      workItemsApi.householdWorkItems(participantId, { page, pageSize }, signal),
    [participantId, page, pageSize, reading]
  );
  const turnTo = (target: number) => (target === page ? void reload() : setPage(target));

  return (
    <div className="grid gap-4">
      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>
              Could not refresh the compliance history — showing it as last read
            </AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}
      {data === null ? (
        error ? null : <TableSkeleton rows={2} />
      ) : data.items.length === 0 ? (
        data.total > 0 ? (
          <PastTheEnd
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            counted={['item', 'items']}
            onPage={turnTo}
          />
        ) : (
          <p className="text-sm">{absent('No work item has been raised for this household.')}</p>
        )
      ) : (
        <>
          <ul aria-label="U4 / compliance history" className="grid gap-4">
            {data.items.map((entry) => (
              <HistoryEntry key={entry.row.id} entry={entry} multi={multi} />
            ))}
          </ul>
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={turnTo}
            quiet
            onMore={more}
          />
        </>
      )}
    </div>
  );
}

/** One item of the history: what it is and where it stands, then its events. */
function HistoryEntry({ entry, multi }: { entry: IWorkItemWithEvents; multi: boolean }) {
  const { row, events } = entry;
  return (
    <li
      data-work-item={row.id}
      className="grid gap-2 rounded-md border border-border px-3 py-2 text-sm"
    >
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Link
          to={workItemPath(row.id)}
          className="font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
        >
          {row.subject}
        </Link>
        <WorkItemStateChip state={row.state} />
        <span className="text-xs text-muted-foreground">
          {row.surveyCode} · {triggerLabel(row.trigger)} · raised {formatDateTime(row.raisedAt)}
          {multi && ` · ${row.participant.displayName}`}
        </span>
      </span>
      {events.length === 0 ? (
        <p>{absent('Nothing has happened to this item yet.')}</p>
      ) : (
        <WorkItemEvents events={events} label={`History: ${row.subject}`} />
      )}
    </li>
  );
}
